import type {
  SxProps, Theme,
} from '@mui/material/styles';
import {
  createTheme, darken, lighten,
} from '@mui/material/styles';
import merge from 'deepmerge';

/**
 * Try to introduce as little colors as possible.
 * If a new color is needed, find inspiration within those palettes:
 * https://mycolor.space/?hex=%2346769A&sub=1
 */
export const ThemeColors = {
  textColor: '#535355',
  primaryColor: '#46769A',
  primaryColorHover: lighten('#46769A', 0.15),
  secondaryColor: '#AECE89',
  backgroundColor: '#e7ecef',
  paperBg: '#ffffff',
  borderColor: 'rgba(0,0,0, 0.08)',
  darkBorderColor: 'rgba(0,0,0, 0.12)',
  selectable: '#FBF4DA',
  tableColumnOther: '#c8d0d0',
  tableRowOther: '#fcfcfc',
  tableBorder: '#e0e0e0',
  tableHeader: '#f7f7f7',
  contentRowOther: '#ebebeb',
  dropPlaceholder: '#46769A',
  deleteWarning: '#ff6961',
  softWarning: '#fa9151',
  softWarningHover: '#fca36d',
  warning: '#46769A',
  textHover: '#edf1f5',
  listItemOther: '#f5f5f5',
  notificationBg: '#fff3cc',
  processingOverlay: 'rgba(237, 241, 245, 0.6)',
  // Used for disabled fields underline / icons
  disabledIndicator: '#dedede',
  dropdownMenuBorder: '#d8dede',
  dropdownMenuBg: '#f5f8fa',
  backdropBg: '#fcfcfc',
  // WSA
  sequenceAnalyserSearch: { hex: '#000000', rgb: [0, 0, 0] },
  sequenceAnalyserSelectionDashedBorder: ['#000000', '#ffffff'],
  sequenceAnalyserSelection: 'rgba(255, 255, 0, 0.5)',
  sequenceAnalyserSelectionNoOpacity: '#FCF499',
  sequenceAnalyserBorder: '#D7D7D7',
  sequenceAnalyserAbbreviatedResidue: [243, 245, 247],
  sequenceAnalyserNavigatorResidue: '#D7D7D7',
  sequenceAnalyserNavigatorGap: '#ffffff',
  // WDA
  dataAnalyzerSelectionBorder: '#000000',
  dataAnalyzerSelection: 'rgba(255, 255, 0, 0.5)',
  dataAnalyzerBorder: '#D7D7D7',
  dataAnalyzerBoxBorder: '#454545',
  dataAnalyzerBoxConnectionBorder: '#9e9e9e',
};

export const ThemeVars = {
  headerEnvHeight: 22,
  headerMenuHeight: 50,
  pageHeaderHeight: 50,
  footerMaxHeight: 159,
  sideLabelWidth: '30%',
  propertyLabelWidth: 260,
  spacing: 8,
  compactFontSize: '0.75rem', // 12px
  compactFontSizeLineHeight: '1.20', // 12px
  /** Needed to be above some Material UI elements */
  fullScreenZIndex: 1201,
  badgeFontSize: '0.8125rem',
};

export default createTheme({
  spacing: ThemeVars.spacing,
  typography: {
    h1: {
      fontSize: 20,
    },
    h2: {
      fontSize: 16,
      fontWeight: 500,
    },
  },
  palette: {
    text: {
      primary: ThemeColors.textColor,
    },
    primary: {
      main: ThemeColors.primaryColor,
    },
    secondary: {
      main: ThemeColors.secondaryColor,
    },
    background: {
      default: ThemeColors.backgroundColor,
    },
    divider: ThemeColors.borderColor,
  },
  shadows: [
    'none',
    '0px 1px 5px 0px rgba(0,0,0,0.0375),0px 1px 1px 0px rgba(0,0,0,0.02625),0px 2px 1px -1px rgba(0,0,0,0.0225)',
    '0px 1px 7px 0px rgba(0,0,0,0.0375),0px 2px 2px 0px rgba(0,0,0,0.02625),0px 3px 1px -2px rgba(0,0,0,0.0225)',
    '0px 1px 7px 0px rgba(0,0,0,0.0375),0px 3px 4px 0px rgba(0,0,0,0.02625),0px 3px 3px -2px rgba(0,0,0,0.0225)',
    '0px 2px 6px -1px rgba(0,0,0,0.0375),0px 4px 5px 0px rgba(0,0,0,0.02625),0px 1px 10px 0px rgba(0,0,0,0.0225)',
    '0px 3px 7px -1px rgba(0,0,0,0.0375),0px 5px 8px 0px rgba(0,0,0,0.02625),0px 1px 14px 0px rgba(0,0,0,0.0225)',
    '0px 3px 7px -1px rgba(0,0,0,0.0375),0px 6px 10px 0px rgba(0,0,0,0.02625),0px 1px 18px 0px rgba(0,0,0,0.0225)',
    '0px 4px 7px -2px rgba(0,0,0,0.0375),0px 7px 10px 1px rgba(0,0,0,0.02625),0px 2px 16px 1px rgba(0,0,0,0.0225)',
    '0px 5px 7px -3px rgba(0,0,0,0.0375),0px 8px 10px 1px rgba(0,0,0,0.02625),0px 3px 14px 2px rgba(0,0,0,0.0225)',
    '0px 5px 8px -3px rgba(0,0,0,0.0375),0px 9px 12px 1px rgba(0,0,0,0.02625),0px 3px 16px 2px rgba(0,0,0,0.0225)',
    '0px 6px 8px -3px rgba(0,0,0,0.0375),0px 10px 14px 1px rgba(0,0,0,0.02625),0px 4px 18px 3px rgba(0,0,0,0.0225)',
    '0px 6px 9px -4px rgba(0,0,0,0.0375),0px 11px 15px 1px rgba(0,0,0,0.02625),0px 4px 20px 3px rgba(0,0,0,0.0225)',
    '0px 7px 10px -4px rgba(0,0,0,0.0375),0px 12px 17px 2px rgba(0,0,0,0.02625),0px 5px 22px 4px rgba(0,0,0,0.0225)',
    '0px 7px 10px -4px rgba(0,0,0,0.0375),0px 13px 19px 2px rgba(0,0,0,0.02625),0px 5px 24px 4px rgba(0,0,0,0.0225)',
    '0px 7px 11px -4px rgba(0,0,0,0.0375),0px 14px 21px 2px rgba(0,0,0,0.02625),0px 5px 26px 4px rgba(0,0,0,0.0225)',
    '0px 8px 11px -5px rgba(0,0,0,0.0375),0px 15px 22px 2px rgba(0,0,0,0.02625),0px 6px 28px 5px rgba(0,0,0,0.0225)',
    '0px 8px 12px -5px rgba(0,0,0,0.0375),0px 16px 24px 2px rgba(0,0,0,0.02625),0px 6px 30px 5px rgba(0,0,0,0.0225)',
    '0px 8px 13px -5px rgba(0,0,0,0.0375),0px 17px 26px 2px rgba(0,0,0,0.02625),0px 6px 32px 5px rgba(0,0,0,0.0225)',
    '0px 9px 13px -5px rgba(0,0,0,0.0375),0px 18px 28px 2px rgba(0,0,0,0.02625),0px 7px 34px 6px rgba(0,0,0,0.0225)',
    '0px 9px 14px -6px rgba(0,0,0,0.0375),0px 19px 29px 2px rgba(0,0,0,0.02625),0px 7px 36px 6px rgba(0,0,0,0.0225)',
    '0px 10px 15px -6px rgba(0,0,0,0.0375),0px 20px 31px 3px rgba(0,0,0,0.02625),0px 8px 38px 7px rgba(0,0,0,0.0225)',
    '0px 10px 15px -6px rgba(0,0,0,0.0375),0px 21px 33px 3px rgba(0,0,0,0.02625),0px 8px 40px 7px rgba(0,0,0,0.0225)',
    '0px 10px 16px -6px rgba(0,0,0,0.0375),0px 22px 35px 3px rgba(0,0,0,0.02625),0px 8px 42px 7px rgba(0,0,0,0.0225)',
    '0px 11px 16px -7px rgba(0,0,0,0.0375),0px 23px 36px 3px rgba(0,0,0,0.02625),0px 9px 44px 8px rgba(0,0,0,0.0225)',
    '0px 11px 17px -7px rgba(0,0,0,0.0375),0px 24px 38px 3px rgba(0,0,0,0.02625),0px 9px 46px 8px rgba(0,0,0,0.0225)',
  ],
  components: {
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: ThemeColors.paperBg,
          border: `1px solid ${ThemeColors.borderColor}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          display: 'inline-block',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: ThemeColors.textHover,
          },
        },
        outlinedPrimary: {
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: lighten(ThemeColors.deleteWarning, 0.95),
          },
        },
        containedPrimary: {
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: ThemeColors.primaryColorHover,
          },
        },
        containedSecondary: {
          color: 'white',
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: darken(ThemeColors.secondaryColor, 0.25),
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&.Mui-disabled': {
            // Keep a normal font weight for disabled fields
            fontWeight: 400,
          },
          '&.Mui-disabled:before': {
            borderBottomStyle: 'solid',
            borderColor: ThemeColors.disabledIndicator,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Remove different shade of grey for icons and use default text color
          color: 'inherit',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: ThemeColors.textHover,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          // Remove different shade of grey for labels and use default text color
          color: 'inherit',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        animated: {
          // Remove different shade of grey for labels and use default text color
          color: 'inherit',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        // Reduce even more the size of dense dropdown as we sometimes have very long ones
        dense: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '.MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
  // Mui v4 values (changed in v5)
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export type SxStyles = SxProps<Theme>;

/**
 * Merge multiple sx objects into one
 */
export function sx(...sxStyles: (SxStyles | undefined)[]) {
  return sxStyles.reduce<SxStyles>((acc, styles) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (styles ? merge(acc as unknown as any, styles as unknown as any) : { ...acc })
  ), {});
}

type SxClasses<T> = { [className in keyof T]: SxStyles };

/**
 * Set types to given styles.
 * It was created following the upgrade from Mui 4 to 5, to help the migration by keeping the existing method name.
 */
export function createStyles<T>(sxClasses: SxClasses<T>) {
  return sxClasses;
}

/**
 * Pass parameters to given styles so they can be used when building the styles.
 * It was created following the upgrade from Mui 4 to 5, to help the migration by keeping the existing method name.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makeStyles<T>(wrapper: (props: any) => SxClasses<T>) {
  return wrapper;
}

/**
 * Utility wrapping a class name to target itself
 *
 * Example:
 *     sx: { [sxClassName(inputClasses.root)]: {...} }
 * Will become:
 *     sx: { ['&.InputBase-root']: {...} }
 */
export function sxClassName(name: string) {
  return `&.${name}`;
}

/**
 * Utility wrapping a class name to target sub component
 *
 * Example:
 *     sx: { [sxClassName(sliderClasses.rail)]: {...} }
 * Will become:
 *     sx: { ['& .MuiSlider-rail']: {...} }
 */
export function sxSubClassName(name: string) {
  return `& .${name}`;
}
